/**
 * ANALYTICS SERVICE - Appmor
 * 
 * Integración real con Google Analytics 4 y Firebase Analytics.
 * Uses @boosting/integrations shared analytics module.
 */

import {
    createAnalyticsService,
    createGoogleAnalytics,
    createFirebaseAnalytics,
    type AnalyticsService,
    type UserProperties,
} from '@boosting/integrations';
import { Logger } from '@boosting/core';

const logger = Logger.getLogger('AppmorAnalytics');

// ============================================
// CONFIGURATION
// ============================================

const GA_MEASUREMENT_ID = 'G-ZNNC1H7XMX'; // Appmor GA4 Property

// ============================================
// ANALYTICS SERVICE INSTANCE
// ============================================

let analyticsInstance: AnalyticsService | null = null;

// Track pending consent for handling timing issues
// (consent granted before analytics initialized)
let pendingConsent: 'granted' | 'denied' | null = null;
let isAnalyticsReady = false;
const readyCallbacks: Array<() => void> = [];

/**
 * Register a callback to be called when analytics is ready
 */
export function onAnalyticsReady(callback: () => void): void {
    if (isAnalyticsReady) {
        callback();
    } else {
        readyCallbacks.push(callback);
    }
}

/**
 * Initialize analytics for Appmor
 * Call this once at app startup
 */
export async function initializeAnalytics(): Promise<void> {
    if (analyticsInstance) {
        logger.warn('Analytics already initialized');
        return;
    }

    try {
        analyticsInstance = createAnalyticsService({
            providers: [
                createGoogleAnalytics({
                    measurementId: GA_MEASUREMENT_ID,
                    debug: import.meta.env.DEV, // Debug mode in development
                    autoPageView: false, // We'll handle page views manually with React Router
                }),
                createFirebaseAnalytics({
                    useExistingApp: true, // Use existing Firebase from firebase.ts
                    debug: import.meta.env.DEV,
                }),
            ],
            requireConsent: true, // GDPR compliance
            debug: import.meta.env.DEV,
        });

        await analyticsInstance.initialize();

        logger.info('Analytics initialized', { measurementId: GA_MEASUREMENT_ID });

        // Apply any pending consent that was set before initialization
        if (pendingConsent === 'granted') {
            analyticsInstance.grantConsent();
            logger.info('Applied pending consent: granted');
        } else if (pendingConsent === 'denied') {
            analyticsInstance.denyConsent();
            logger.info('Applied pending consent: denied');
        }
        pendingConsent = null;

        // Mark as ready and notify callbacks
        isAnalyticsReady = true;
        readyCallbacks.forEach(cb => cb());
        readyCallbacks.length = 0;

    } catch (error) {
        logger.error('Failed to initialize analytics', error as Error);
        // Still mark as ready (failed state) to unblock waiting components
        isAnalyticsReady = true;
        readyCallbacks.forEach(cb => cb());
        readyCallbacks.length = 0;
    }
}

/**
 * Get the analytics service instance
 */
export function getAnalytics(): AnalyticsService | null {
    return analyticsInstance;
}

// ============================================
// CONVENIENCE FUNCTIONS
// ============================================

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
    if (!analyticsInstance) return;
    analyticsInstance.track(eventName, params as Record<string, string | number | boolean | undefined>);
}

/**
 * Track a page view - call this on route changes
 */
export function trackPageView(pagePath?: string, pageTitle?: string): void {
    if (!analyticsInstance) return;
    analyticsInstance.trackPageView({
        page_path: pagePath || window.location.pathname,
        page_title: pageTitle || document.title,
    });
}

/**
 * Set user ID after login
 */
export function setAnalyticsUser(userId: string, properties?: Partial<UserProperties>): void {
    if (!analyticsInstance) return;

    analyticsInstance.setUserId(userId);

    if (properties) {
        analyticsInstance.setUserProperties(properties as UserProperties);
    }
}

/**
 * Clear user on logout
 */
export function clearAnalyticsUser(): void {
    if (!analyticsInstance) return;
    analyticsInstance.setUserId(null);
}

/**
 * Grant analytics consent (user accepted cookies)
 */
export function grantAnalyticsConsent(): void {
    if (!analyticsInstance) {
        // Store pending consent to apply when initialized
        pendingConsent = 'granted';
        logger.info('Analytics consent granted (pending initialization)');
        return;
    }
    analyticsInstance.grantConsent();
    logger.info('Analytics consent granted');
}

/**
 * Deny analytics consent (user rejected cookies)
 */
export function denyAnalyticsConsent(): void {
    if (!analyticsInstance) {
        // Store pending consent to apply when initialized
        pendingConsent = 'denied';
        logger.info('Analytics consent denied (pending initialization)');
        return;
    }
    analyticsInstance.denyConsent();
    logger.info('Analytics consent denied');
}

// ============================================
// PREDEFINED EVENTS
// ============================================

/**
 * Track user login
 */
export function trackLogin(method: 'email' | 'google' | 'phone'): void {
    trackEvent('login', { method });
}

/**
 * Track user signup
 */
export function trackSignup(method: 'email' | 'google' | 'phone', userRole?: string): void {
    trackEvent('sign_up', { method, user_role: userRole });
}

/**
 * Track feature usage
 */
export function trackFeatureUsed(featureName: string, details?: Record<string, unknown>): void {
    trackEvent('feature_used', { feature_name: featureName, ...details });
}

/**
 * Track errors
 */
export function trackError(errorType: string, errorMessage: string, component?: string): void {
    trackEvent('error', {
        error_type: errorType,
        error_message: errorMessage,
        component,
    });
}

/**
 * Track therapist-specific events
 */
export const therapistAnalytics = {
    patientCreated: () => trackEvent('therapist_patient_created'),
    sessionNoteCreated: () => trackEvent('therapist_session_note_created'),
    practiceAssigned: (practiceType: string) => trackEvent('therapist_practice_assigned', { practice_type: practiceType }),
    appointmentBooked: () => trackEvent('therapist_appointment_booked'),
    articlePublished: () => trackEvent('therapist_article_published'),
    profileUpdated: () => trackEvent('therapist_profile_updated'),
};

/**
 * Track patient-specific events
 */
export const patientAnalytics = {
    practiceStarted: (practiceType: string) => trackEvent('patient_practice_started', { practice_type: practiceType }),
    practiceCompleted: (practiceType: string, durationSec: number) => trackEvent('patient_practice_completed', { practice_type: practiceType, duration_sec: durationSec }),
    moodRecorded: (mood: string) => trackEvent('patient_mood_recorded', { mood }),
    aiAssistantUsed: () => trackEvent('patient_ai_assistant_used'),
};
